import React from 'react';
import type { RefObject } from 'react';
import type {
  ChildrenProp,
  ClassNameProp,
  MaskableComponentProps,
  OptimizedMaskableProps
} from '../common-types';
import type { State } from 'zustand';

/**
 * Type for a {@link LoadMask}.
 */
export type LoadMaskProps = Required<
  Omit<MaskableComponentProps, 'className'>
> &
  ClassNameProp;

/**
 * A simple load mask.
 *
 * @param masked whether to show the load mask
 * @param className className
 * @param props leftover props
 * @constructor
 */
export function LoadMask({
  masked,
  className = '',
  ...props
}: Readonly<LoadMaskProps>): React.JSX.Element {
  return (
    <div
      {...props}
      className={`loadmask-root ${className}`.trim()}
      style={masked ? undefined : { display: 'none' }}
      aria-busy={masked}
    >
      <div className="loadmask-inner">
        <div className="">
          <div className="">
            <span className="x-loading-top" />
            <span className="x-loading-right" />
            <span className="x-loading-bottom" />
            <span className="x-loading-left" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A load mask which directly listens to store changes.
 *
 * @param masked whether to show the load mask
 * @constructor
 */
export function OptimizedLoadMask<T extends State>({
  useStore,
  selector,
  className,
  ...props
}: Readonly<OptimizedMaskableProps<T>>): React.JSX.Element {
  const masked = useStore(selector);
  return <LoadMask {...props} masked={masked} className={className} />;
}

/**
 * Type for the props of a {@link Maskable}.
 */
export type MaskableProps = Required<
  Omit<MaskableComponentProps, 'className'>
> &
  Required<ChildrenProp> &
  ClassNameProp & { ref?: RefObject<HTMLDivElement | null> };

/**
 * A HOC that makes it easy to show a load mask over some content.
 *
 * THIS CAN BE EXPENSIVE:
 * All children are re-rendered whenever masked changes.
 * If children are expensive to render, either cache them using {@link React.memo} or use {@link OptimizedMaskable},
 * in which case the LoadMask directly listens to store changes.
 *
 * @param masked whether to show the load mask
 * @param children the body that should be maskable
 * @param className css class name
 * @param props leftover props
 * @constructor
 */
export function Maskable({
  masked,
  children,
  className = '',
  ...props
}: Readonly<MaskableProps>): React.JSX.Element {
  return (
    <div {...props} style={{ position: 'relative' }} className={className}>
      {children}
      <LoadMask masked={masked} />
    </div>
  );
}

type AdditionalOptimizedMaskableProps = {
  /**
   * Whether to add width,height: 100% and overflow: hidden on the wrapping div.
   *
   * This is important for portlet-wide load masks.
   */
  fullSizeOverflowHidden?: boolean;
};

/**
 * A HOC that makes it easy to show a load mask over some content.
 *
 * This is an optimized version taking a store and selector, which allows the {@link LoadMask} to directly listen to changes.
 * Unnecessary re-renders of the children are therefore avoided.
 *
 * @param masked whether to show the load mask
 * @param children the body that should be maskable
 * @param fullSizeOverflowHidden whether to add height and overflow styles. Use for portlet-wide load masks!
 * @param props leftover props
 * @constructor
 */
export function OptimizedMaskable<T extends State>({
  useStore,
  selector,
  children,
  className,
  fullSizeOverflowHidden = false,
  ...props
}: Readonly<
  OptimizedMaskableProps<T> &
    Required<ChildrenProp> &
    AdditionalOptimizedMaskableProps
>): React.JSX.Element {
  let additionalStyles = {};
  if (fullSizeOverflowHidden) {
    additionalStyles = {
      height: '100%',
      width: '100%',
      overflow: 'hidden'
    };
  }
  return (
    <div
      {...props}
      style={{ ...additionalStyles, position: 'relative' }}
      className={className}
    >
      {children}
      <OptimizedLoadMask<T> selector={selector} useStore={useStore} />
    </div>
  );
}
